import React, { type FunctionComponent, useEffect, useState, useMemo } from 'react'
import moment from 'moment/moment'
import { spacing, useBooleanState } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import {
  ShiftDetailsFormContainer,
  ShiftForm,
  StyledErrorMessage,
} from 'domains/shifts/components/recurring-shifts/ShiftDetailsForm.styles'
import {
  type PartnerPickRecurringShiftsRecurringShift,
  type PartnerPickRecurringShiftsRecurringShiftConflict,
  type PartnerPickRecurringShiftsSingleShiftConflict,
} from 'domains/partner-pick-devices/api'
import { ShiftAllocationTypeControls } from 'domains/shifts/components/recurring-shifts/ShiftAllocationTypeControls'
import { AllocationType } from 'domains/shifts/components/types'
import { getRepeatTypeFromDaysOfWeek } from 'domains/shifts/components/utils/shiftsUtils'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { RecommendationDeviceDropdown } from 'domains/shifts/components/components/recommended-shifts/RecommendationDeviceDropdown'
import { RecommendationShiftFormNotice } from 'domains/shifts/components/components/recommended-shifts/RecommendationShiftFormNotice'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { RecommendationTypeEnum } from 'domains/shifts/api'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { useShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'
import { ShiftDetailsFormStylesProvider } from 'domains/shifts/components/contexts/ShiftDetailsFormStylesContext'
import { ShiftDetailsTimeRange } from './ShiftDetailsTimeRange'
import { ShiftRepeatDropDown } from './ShiftRepeatDropDown'
import { ShiftEndDateControls } from './ShiftEndDateControls'
import { ButtonsFooter } from './ButtonsFooter'
import { CustomDaysDropdown } from './CustomDaysDropdown'
import { FormHeader } from './FormHeader'
import { usePartnerPickRecurringShiftsForm } from './usePartnerPickRecurringShiftForm.hooks'
import {
  type PartnerPickRecurringShiftsFormData,
  type PartnerPickRecurringShiftsFormProps,
  RepeatShiftEnum,
} from './recurringShift.types'
import {
  getRecurringShiftDescription,
  TIME_DISPLAY_FORMAT,
} from './partnerPickRecurringShiftForm.utils'
import { usePartnerPickRecurringShiftsConflictCheck } from './usePartnerPickRecurringShiftsConflictCheck.hooks'

export enum ConfirmationModalType {
  Delete = 'DELETE',
  Regular = 'REGULAR',
  Conflicts = 'CONFLICTS',
  Edit = 'EDIT',
  Recommendation = 'RECOMMENDATION',
}

const StyledShiftName = styled.div({
  padding: spacing.X4,
  '> div:not(:last-child)': {
    marginBottom: '5px',
  },
})
export interface OpenConfirmationModalArgs {
  modalType: ConfirmationModalType
}

export const PartnerPickRecurringShiftsForm: FunctionComponent<
  PartnerPickRecurringShiftsFormProps
> = ({
  onCancel,
  onSave,
  onDelete,
  date,
  warehouseLocationId,
  formData,
  onAddNonRecurringShift,
  onUpdateNonRecurringShift,
  getLocalShopperDetails,
  deleteNonRecurringShift,
  zoneOffset,
  onApiError,
  devicePrioritizationEnabled,
  recurShiftsToBeNonRecur,
  ultrafastCrossDayDeliveryEnabled,
  ...props
}) => {
  const dayOfWeek = moment(date).format('dddd') // ex. Monday
  const messages = useDomainMessages(
    {
      newShiftTitle: 'shiftsDomain.prompt.newShift',
      editShiftTitle: 'shiftsDomain.prompt.editShift',
      from: 'shiftsDomain.prompt.from',
      to: 'shiftsDomain.prompt.to',
      repeatShift: 'shiftsDomain.prompt.repeatShift',
      repeatsOn: 'shiftsDomain.prompt.repeatsOn',
      cancel: 'shiftsDomain.prompt.cancel',
      save: 'shiftsDomain.prompt.save',
      doesNotRepeat: 'shiftsDomain.prompt.doesNotRepeat',
      weeklyOnDay: 'shiftsDomain.prompt.weeklyOnDay',
      everyWeekday: 'shiftsDomain.prompt.everyWeekday',
      daily: 'shiftsDomain.prompt.daily',
      custom: 'shiftsDomain.prompt.custom',
      delete: 'shiftsDomain.prompt.delete',
      checking: 'shiftsDomain.confirmation.modal.checking',
      startsFromLabel: 'shiftsDomain.confirmation.modal.startsFromLabel',
      endsAtLabel: 'shiftsDomain.confirmation.modal.endsAtLabel',
      noEndDate: 'shiftsDomain.confirmation.modal.noEndDate',
      hasStartDate: 'shiftsDomain.confirmation.modal.hasStartDate',
      hasEndDate: 'shiftsDomain.confirmation.modal.hasEndDate',
      errorDeletingShift: 'shiftsDomain.prompt.error.shift.delete',
      errorSavingShift: 'shiftsDomain.prompt.error.shift.saving',
      errorSavingShiftChanges: 'shiftsDomain.prompt.error.shift.saving.changes',
      device: 'shiftsDomain.capacityRecommendation.device',
      addPickingShift: 'shiftsDomain.capacityRecommendation.addPickingShift',
      addRunningShift: 'shiftsDomain.capacityRecommendation.addRunningShift',
    },
    {
      dayOfWeek,
    }
  )

  const {
    storeHours: { regularStoreOpen, regularStoreClose },
  } = useShiftManagementContext()

  const [errorMessage, setErrorMessage] = useState('')
  const [nonRecurringShiftErrorMessage, setNonRecurringShiftErrorMessage] = useState('')
  const {
    shiftsState: {
      shiftType,
      shopperIdBeingEdited: shopperId,
      shiftIdBeingEdited: shiftId,
      recurringShiftIdBeingEdited: recurringShiftId,
    },
  } = useShiftsStateContext()

  const {
    watch,
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState,
    submitForm,
    deleteRecurringShift,
    formSubmitData,
    formSubmitSuccess,
    formSubmitLoading,
    formSubmitError,
    shiftDeleteLoading,
    shiftDeleteSuccess,
    shiftDeleteError,
  } = usePartnerPickRecurringShiftsForm({
    formData: formData,
    warehouseLocationId,
    shiftId,
    recurringShiftId,
    shiftType,
    shopperId,
    onAddNonRecurringShift,
    date,
    onShiftSave: onSave,
    onApiError,
    setErrorMessage,
    devicePrioritizationEnabled,
  })

  const {
    shiftsState: { recommendationType },
  } = useShiftsStateContext()

  const {
    fetchPartnerPickRecurringShiftConflicts,
    conflictsData,
    conflictsLoading,
    conflictsError,
  } = usePartnerPickRecurringShiftsConflictCheck()
  const warehouseContext = useWarehouseContext()
  const {
    shiftsState: { isOpenedByDragging, isEditMode, isRecommendationMode },
  } = useShiftsStateContext()

  const warehouseLocation = useMemo(
    () =>
      warehouseContext.warehouse?.warehouseLocations.find(whl => whl.id === warehouseLocationId),
    [warehouseContext.warehouse?.warehouseLocations, warehouseLocationId]
  )

  const conflictIds = useMemo(
    () =>
      conflictsData?.partnerPickRecurringShiftConflictCheck
        ? Object.values(conflictsData?.partnerPickRecurringShiftConflictCheck)
            .filter(
              (
                value
              ): value is Array<
                PartnerPickRecurringShiftsRecurringShiftConflict &
                  PartnerPickRecurringShiftsSingleShiftConflict
              > => Array.isArray(value)
            )
            .reduce(
              (ids, conflictShifts) => ids.concat(conflictShifts.map(({ id }) => id)),
              [] as string[]
            )
        : [],
    [conflictsData]
  )
  useEffect(() => {
    if (formSubmitError) {
      setErrorMessage(isEditMode ? messages.errorSavingShiftChanges : messages.errorSavingShift)
    }
    if (shiftDeleteError) {
      setErrorMessage(messages.errorDeletingShift)
    }
    if (conflictsError) {
      setErrorMessage(messages.errorSavingShift) /// we do not show a specific error for this case.
    }
  }, [formSubmitError, shiftDeleteError, conflictsError, isEditMode]) // eslint-disable-line

  const getConflictCheckVariables = () => ({
    existingRecurringShiftId: recurringShiftId?.toString(),
    existingDriverShiftId: shiftId?.toString(),
    driverId: shopperId?.toString(),
    warehouseLocationId,
    daysOfWeek: watch('daysOfWeek')?.map(obj => obj.value) || [],
    shiftStartSecondsSinceMidnight: watch('from').value,
    shiftLengthSeconds: watch('to').value - watch('from').value,
    recurEndDate: watch('recurEndDate') ? moment(watch('recurEndDate'))?.toISOString() : undefined,
    recurStartDate: moment(date).toISOString(),
  })

  const checkConflicts = () => {
    const variables = getConflictCheckVariables()
    fetchPartnerPickRecurringShiftConflicts({
      variables,
    })
  }

  useEffect(() => {
    if (formSubmitSuccess && shopperId) {
      onSave(
        formSubmitData as PartnerPickRecurringShiftsRecurringShift,
        shopperId,
        shiftId,
        conflictIds
      )
    }
  }, [formSubmitData, formSubmitSuccess, shiftId, conflictIds, onSave, shopperId])

  useEffect(() => {
    if (shiftDeleteSuccess) {
      // currently RecurringShiftUpdateMutation does not return `driverShiftId` as part of its response
      // as a result, when we merge two recurring shift into one shift, the frontend is not aware of the new `driverShiftId`
      // and this caused the `shiftId` for the newly merged shift to be null.
      // in which case we can pass `recurringShiftId` instead to delete the shift cell from the UI
      onDelete(shopperId, shiftId ?? recurringShiftId)
    }
  }, [shiftId, onDelete, shiftDeleteSuccess, recurringShiftId, shopperId])

  const getCurrentShiftDescription = () => {
    const shiftStartSecondsSinceMidnight = getValues('from').value,
      shiftLengthSeconds = getValues('to').value - getValues('from').value
    const daysOfWeek = getValues('daysOfWeek').map(obj => obj.value)
    const { timespan, description } = getRecurringShiftDescription({
      shiftStartSecondsSinceMidnight,
      shiftLengthSeconds,
      daysOfWeek,
      date,
    })

    const recurEndDate = getValues('recurEndDate')
    const recurStartDate =
      isEditMode && formData
        ? formData.recurStartDate
          ? moment(formData.recurStartDate)
          : undefined
        : moment(date)
    return (
      <StyledShiftName>
        <div>{timespan}</div>
        <div>{description}</div>
        {renderStartAndEndDateString(recurStartDate, recurEndDate)}
      </StyledShiftName>
    )
  }

  const renderStartAndEndDateString = (recurStartDate, recurEndDate) => {
    const DATE_FORMAT = 'ddd D MMM'
    if (recurStartDate && recurEndDate) {
      return `${messages.startsFromLabel} ${moment(recurStartDate).format(DATE_FORMAT)} ${
        messages.endsAtLabel
      } ${moment(recurEndDate).format(DATE_FORMAT)}`
    }

    // no end date selected, start date should always be present
    return (
      <>
        <div>{`${messages.hasStartDate} ${moment(recurStartDate).format(DATE_FORMAT)}`}</div>
        <div>{messages.noEndDate}</div>
      </>
    )
  }

  const getStartAndEndDate = () => {
    const startSecondsSinceMidnight = watch('from').value
    const endSecondsSinceMidnight = watch('to').value
    // This fixes specific case where shift ends at 12am and we end up sending start > end time as end becomes midnight of previous day,
    // this fix will allow  scheduling shifts from 10pm-12am
    if (ultrafastCrossDayDeliveryEnabled) {
      const startMoment = moment.utc(date).add(startSecondsSinceMidnight, 'seconds')

      // We used `seconds since midnight` before, but it could cause issue when the `end time` is right at mid-night
      // If it's right at midnight, the `seconds since midnight` will be 0 even though it's the next day
      // We are now using `seconds since start time` (secondsInADay - startSecondsSinceMidnight) to calculate the `end time`
      const secondsInADay = 60 * 60 * 24
      let endMoment =
        endSecondsSinceMidnight === 0
          ? moment(startMoment).add(secondsInADay - startSecondsSinceMidnight, 'seconds')
          : moment.utc(date).add(endSecondsSinceMidnight, 'seconds')

      // Let's say start time is 10pm and end time is 1am
      // In this case, we will add a day to the end time
      if (endSecondsSinceMidnight < startSecondsSinceMidnight && endSecondsSinceMidnight !== 0) {
        endMoment = moment(endMoment).add(1, 'day')
      }

      startMoment.add(-1 * zoneOffset, 'h')
      endMoment.add(-1 * zoneOffset, 'h')

      return {
        start: startMoment.toDate(),
        end: endMoment.toDate(),
      }
    }

    // Need to map this date to corresponding utc date so API params can be correct UTC values corresponding to target/store timezone
    return {
      start: moment
        .utc(date)
        .add(startSecondsSinceMidnight, 'seconds')
        .add(-1 * zoneOffset, 'h')
        .toDate(),
      end: moment
        .utc(date)
        .add(endSecondsSinceMidnight, 'seconds')
        .add(-1 * zoneOffset, 'h')
        .toDate(),
    }
  }

  const { start, end } = getStartAndEndDate()
  const repeatsShift = getValues('repeatShift')

  const daysOfWeek = getValues('daysOfWeek')
  const repeatType = getRepeatTypeFromDaysOfWeek(daysOfWeek)

  const [
    isDeleteNonRecurringShiftLoading,
    showDeleteNonRecurringShiftLoading,
    hideDeleteNonRecurringShiftLoading,
  ] = useBooleanState(false)
  const handleDeleteNonRecurringShift = async () => {
    showDeleteNonRecurringShiftLoading()
    deleteNonRecurringShift(shopperId, shiftId)
      .then(() => hideDeleteNonRecurringShiftLoading())
      .catch(err => {
        const errMessage = err.message || messages.errorDeletingShift
        setNonRecurringShiftErrorMessage(errMessage)
      })
  }

  const [
    isUpdateNonRecurringShiftLoading,
    showUpdateNonRecurringShiftLoading,
    hideUpdateNonRecurringShiftLoading,
  ] = useBooleanState(false)
  const handleUpdateNonRecurringShift = async () => {
    showUpdateNonRecurringShiftLoading()
    const onCall =
      devicePrioritizationEnabled && getValues('shiftAllocationType') === AllocationType.OnCall
    onUpdateNonRecurringShift(shopperId, shiftId, recurringShiftId, start, end, onCall)
      .then(() => hideUpdateNonRecurringShiftLoading())
      .catch(err => {
        const errMessage = err.message || messages.errorSavingShiftChanges
        setNonRecurringShiftErrorMessage(errMessage)
      })
  }

  const [
    isAddNonRecurringShiftLoading,
    showAddNonRecurringShiftLoading,
    hideAddNonRecurringShiftLoading,
  ] = useBooleanState(false)

  const handleAddNonRecurringShift = async () => {
    const onCall =
      devicePrioritizationEnabled && getValues('shiftAllocationType') === AllocationType.OnCall

    showAddNonRecurringShiftLoading()
    onAddNonRecurringShift(shopperId, start, end, onCall)
      .then(() => {
        hideAddNonRecurringShiftLoading()
      })
      .catch(err => {
        const errMessage = err.message || messages.errorSavingShift
        setNonRecurringShiftErrorMessage(errMessage)
      })
  }

  const isNonRecurringShift = !watch('daysOfWeek') || watch('daysOfWeek').length === 0

  const isNonRecurringShiftBeingConvertedToRecurring =
    !!shiftId && !recurringShiftId && watch('daysOfWeek')?.length > 0

  // update recurring shift when only common fields are changed (To, From, and Shift Type), prompt user for
  // if the change applies to only this instance or all instances of a recurring shift
  const onlyCommonFieldsDirty = () => {
    const commonFields: (keyof PartnerPickRecurringShiftsFormData)[] = [
      'from',
      'to',
      'shiftAllocationType',
    ]
    const dirtyFields = Array.from(
      formState.dirtyFields
    ) as (keyof PartnerPickRecurringShiftsFormData)[]

    return dirtyFields.every(field => commonFields.includes(field))
  }

  const isCustomdaysDropdownSelected =
    repeatsShift?.value === RepeatShiftEnum.Custom || repeatType === RepeatShiftEnum.Custom
  const isCustomdaysOptionsSelected = isCustomdaysDropdownSelected && daysOfWeek.length !== 0

  const isFormDirtyInEditMode = isEditMode && formState.isValid && formState.dirty
  const isFormValidInNonEditMode = !isEditMode && formState.isValid
  const isSubmitEnabled =
    (isOpenedByDragging || isFormDirtyInEditMode || isFormValidInNonEditMode) &&
    (!isCustomdaysDropdownSelected || isCustomdaysOptionsSelected)

  const recommendationTitleByType: { [key in RecommendationTypeEnum]: string } = {
    [RecommendationTypeEnum.NoRunner]: messages.addRunningShift,
    [RecommendationTypeEnum.DemandSurge]: messages.addPickingShift,
  }
  const headerTitle = isRecommendationMode
    ? recommendationTitleByType[recommendationType]
    : isEditMode
    ? messages.editShiftTitle
    : messages.newShiftTitle

  return (
    <ShiftDetailsFormStylesProvider>
      <ShiftDetailsFormContainer>
        <ShiftForm data-testid="shopper-shifts-shift-form">
          <FormHeader onCancel={onCancel} title={headerTitle} />
          {isRecommendationMode && <RecommendationShiftFormNotice type={recommendationType} />}
          {isRecommendationMode && (
            <RecommendationDeviceDropdown
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              date={date}
              label={messages.device}
              register={register}
              control={control}
              warehouseLocation={warehouseLocation.name}
            />
          )}
          <ShiftDetailsTimeRange
            displayFormat={TIME_DISPLAY_FORMAT}
            regularStoreOpen={regularStoreOpen}
            regularStoreClose={regularStoreClose}
            fromLabel={messages.from}
            toLabel={messages.to}
            register={register}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            isEditMode={isEditMode}
            date={date}
            zoneOffset={zoneOffset}
            {...props}
          />

          {devicePrioritizationEnabled && (
            <ShiftAllocationTypeControls
              value={getValues('shiftAllocationType')}
              setValue={setValue}
            />
          )}

          <ShiftRepeatDropDown
            watch={watch}
            setValue={setValue}
            date={date}
            label={messages.repeatShift}
            register={register}
            control={control}
            recurShiftsToBeNonRecur={recurShiftsToBeNonRecur}
            recurringShiftId={recurringShiftId}
          />

          {isCustomdaysDropdownSelected && (
            <CustomDaysDropdown
              date={date}
              watch={watch}
              register={register}
              getValues={getValues}
              control={control}
              setValue={setValue}
              label={messages.repeatsOn}
              repeatsShift={repeatsShift}
            />
          )}

          {!isNonRecurringShift && (
            <ShiftEndDateControls
              setValue={setValue}
              register={register}
              date={date}
              getValues={getValues}
            />
          )}

          {nonRecurringShiftErrorMessage && (
            <StyledErrorMessage> {nonRecurringShiftErrorMessage}</StyledErrorMessage>
          )}

          <ButtonsFooter
            isEditMode={isEditMode}
            isNonRecurringShift={isNonRecurringShift}
            isNonRecurringShiftBeingConvertedToRecurring={
              isNonRecurringShiftBeingConvertedToRecurring
            }
            onlyCommonFieldsDirty={onlyCommonFieldsDirty()}
            shopperId={shopperId}
            date={date}
            zoneOffset={zoneOffset}
            formSubmitLoading={
              formSubmitLoading || isUpdateNonRecurringShiftLoading || isAddNonRecurringShiftLoading
            }
            shiftDeleteLoading={shiftDeleteLoading || isDeleteNonRecurringShiftLoading}
            conflictsLoading={conflictsLoading}
            getLocalShopperDetails={getLocalShopperDetails}
            addNonRecurringShift={handleAddNonRecurringShift}
            updateNonRecurringShift={handleUpdateNonRecurringShift}
            deleteNonRecurringShift={handleDeleteNonRecurringShift}
            deleteRecurringShift={() => deleteRecurringShift(recurringShiftId, date)}
            currentShiftDescription={getCurrentShiftDescription()}
            checkConflicts={checkConflicts}
            isSubmitEnabled={isSubmitEnabled}
            onCancel={onCancel}
            submitForm={handleSubmit(submitForm)}
            conflictsData={conflictsData}
            errorMessage={errorMessage}
          />
        </ShiftForm>
      </ShiftDetailsFormContainer>
    </ShiftDetailsFormStylesProvider>
  )
}
